import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { getTenantConfig, setTenantContext, brandingFromConfig, type TenantConfigError } from '@/lib/api'
import { rememberTenantSlug } from '@/lib/publicBranding'
import type { TenantConfig, TenantBranding, UserRole } from '@/lib/api'

export type { UserRole }

/** Signed in, but the business has no client record under this email. */
export interface NoRecord {
  email: string | null
  businessName: string | null
}

export interface RoleState {
  /** 'no_record' is NOT 'error': retrying cannot help, so it gets its own screen. */
  status: 'loading' | 'resolved' | 'error' | 'no_record'
  noRecord?: NoRecord
  userRole: UserRole | 'unknown'
  permissions: string[]
  tenantConfig: TenantConfig | null
  branding: TenantBranding | null
  setupRequired: boolean
  provisioning: boolean
}

// ─── Module-level singleton store ────────────────────────────────────────────
// Fetched once per auth state change, shared across all useRole() consumers.

const _initial: RoleState = {
  status: 'loading',
  userRole: 'unknown',
  permissions: [],
  tenantConfig: null,
  branding: null,
  setupRequired: false,
  provisioning: false,
}

let _state: RoleState = { ..._initial }
let _tenantSlug: string | null = null
const _listeners = new Set<() => void>()
let _authListenerRegistered = false
let _inflight = false

function _setState(next: RoleState) {
  _state = next
  _listeners.forEach(fn => fn())
}

async function _detect() {
  if (_inflight) return
  _inflight = true
  _setState({ ..._state, status: 'loading' })

  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      _setState({ ..._initial, status: 'resolved' })
      return
    }

    const config = await getTenantConfig(_tenantSlug ?? undefined)

    // Propagate resolved slug/schema for subsequent API calls
    if (config.slug) setTenantContext({ slug: config.slug })
    else if (config.schema_name) setTenantContext({ schema: config.schema_name })

    // Remember which business this browser belongs to, so the NEXT sign-in
    // screen can wear its brand — signed out, there is nothing else to go on.
    rememberTenantSlug(config.slug)

    const branding = brandingFromConfig(config)
    const userRole: UserRole = config.user_role ?? 'customer'
    const isOwnerOrStaff = userRole === 'owner' || userRole === 'staff'

    _setState({
      status: 'resolved',
      userRole,
      permissions: config.permissions ?? [],
      tenantConfig: isOwnerOrStaff ? config : config,
      branding,
      setupRequired: userRole === 'owner' && !config.setup_complete && config.schema_ready !== false,
      provisioning: isOwnerOrStaff && config.schema_ready === false,
    })
  } catch (err) {
    // Read by shape, not instanceof: a failure must reach the error state whatever threw it.
    const failure = (err as Partial<TenantConfigError> | null)?.failure ?? null
    if (failure?.reason === 'customer_not_found') {
      _setState({
        ..._initial,
        status: 'no_record',
        noRecord: { email: failure.email ?? null, businessName: failure.business_name ?? null },
      })
    } else {
      _setState({ ..._initial, status: 'error' })
    }
  } finally {
    _inflight = false
  }
}

function _ensureAuthListener() {
  if (_authListenerRegistered) return
  _authListenerRegistered = true
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      _inflight = false
      _tenantSlug = null
      _setState({ ..._initial, status: 'resolved' })
    } else if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
      if (_state.status === 'resolved' && _state.userRole !== 'unknown') return
      _detect()
    }
  })
}

// ─── Slug injection ───────────────────────────────────────────────────────────
// Call from the [tenant] layout before the hook mounts to prime the slug.

export function primeTenantSlug(slug: string) {
  if (_tenantSlug === slug) return
  _tenantSlug = slug
  setTenantContext({ slug })
  // Reset so the next _detect() picks up the new slug
  _inflight = false
  _setState({ ..._initial, status: 'loading' })
  _detect()
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useRole() {
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    _ensureAuthListener()

    const listener = () => forceUpdate(n => n + 1)
    _listeners.add(listener)

    return () => { _listeners.delete(listener) }
  }, [])

  return { ..._state, refresh: _detect }
}
